import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { profileApi, ProfileModel, s3Api } from '@pukka/api';
import { Button, Checkbox, DatePicker, ImageUpload, Input, Label, Text } from '@pukka/ui-components';
import { OutletSelectorV2Modal } from '@pukka/ui-composite';
import { twMerge } from 'tailwind-merge';

import { CustomerDisplayFormSchemaType } from './customerDisplayFormSchema';
import MaxCampaignError from './MaxCampaignError';
import { today } from './utils';

interface CustomerDisplayFormProps {
  readonly errorOutlets: number[];
}

function CustomerDisplayForm({ errorOutlets }: CustomerDisplayFormProps) {
  const { data: profile } = profileApi.useProfile();

  const {
    register,
    formState: { errors },
    watch,
    control,
  } = useFormContext<CustomerDisplayFormSchemaType>();

  const setTimePeriod = watch('setTimePeriod');

  const mappedOutlets = useMemo(() => {
    return (
      profile?.outlets?.reduce(
        (prev, acc) => {
          prev[acc.id || ''] = acc;

          return prev;
        },
        {} as Record<string, ProfileModel.Outlet>,
      ) || {}
    );
  }, [profile]);

  const mappedErrorOutlets = errorOutlets.map((outletId) => mappedOutlets[outletId].name);

  const isTestEnv = !import.meta.env.MODE;

  return (
    <div className="px-4 py-2 text-sm">
      {/* Name section */}
      <div className="mb-5">
        <div className="mb-4 border-b border-neutral-200 pb-2 text-xs text-neutral-500">CAMPAIGN SLIDERS NAME</div>
        <Controller
          control={control}
          name="name"
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <>
                <Input
                  value={value}
                  onChange={(e) => {
                    const input = e.target.value;
                    const regEx = /^[a-zA-Z0-9 ]*$/;

                    if (input.match(regEx)) {
                      onChange(input);
                    }
                  }}
                  variant={errors.name ? 'error' : 'default'}
                  type="text"
                  placeholder="Name"
                  className="w-full"
                  maxLength={25}
                />
                {error && (
                  <small className="text-red-500" data-testid="error-message">
                    {error.message}
                  </small>
                )}
              </>
            );
          }}
        />
      </div>

      {/* Image section */}
      <div className="mb-5">
        <div className="mb-4 border-b border-neutral-200 pb-2 text-xs text-neutral-500">UPLOAD IMAGES</div>
        <Text>Customer Display Images</Text>
        <Text variant="caption-1" color="default" className="mb-1 mt-2">
          Add up to <b>5 photos</b>. Size min <b>800x800px</b> and max <b>8MB.</b> Format: PNG and JPEG.
        </Text>
        <Controller
          control={control}
          name="images"
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <>
                <ImageUpload
                  images={value}
                  isEmptyError={!!error}
                  ordering={false}
                  onUpdate={(files, status) => {
                    if (status === 'valid') {
                      // @ts-ignore
                      const imageList = files.map((file) => ({ url: file.meta?.url ?? file.url, name: file.name }));

                      onChange(imageList);
                    }
                  }}
                  className={twMerge('w-full', error && 'border-red-500')}
                  rule={
                    isTestEnv
                      ? undefined
                      : {
                          maxCount: 5,
                          minHeight: [800, 'Failed to upload image. Min height required is 800px'],
                          minWidth: [800, 'Failed to upload image. Min width required is 800px'],
                          maxSize: [8_000_000, 'Failed to upload image. Max size is 8MB'],
                          fileTypes: [['image/png', 'image/jpeg'], 'Failed to upload image. Allowed format: PNG and JPEG'],
                          errorMessage: 'Failed to upload image. Min size required is 800x800px, max 8MB',
                        }
                  }
                  uploadHandler={async (image, onProgress) => {
                    const outletId = profile?.outlets?.[0].id;

                    if (outletId) {
                      return s3Api.uploadImage(image, outletId, onProgress);
                    }

                    return isTestEnv ? Promise.resolve() : Promise.reject();
                  }}
                />
                {error && (
                  <small className="text-red-500" data-testid="error-message">
                    {error.message}
                  </small>
                )}
              </>
            );
          }}
        />
        <Label as="p" className="my-2.5">
          Image Slide Duration
        </Label>
        <Controller
          control={control}
          name="duration"
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <>
                <div className="flex gap-2">
                  <Input
                    value={value}
                    onChange={(e) => {
                      const input = e.target.value;
                      const inputNumber = +input;

                      onChange(inputNumber > 60 ? '60' : input);
                    }}
                    type="number"
                    variant={error ? 'error' : 'default'}
                    placeholder="Duration"
                    className="w-24"
                    min={1}
                    max={60}
                  />
                  <div className="leading-[42px]">seconds</div>
                </div>
                {error && (
                  <small className="text-red-500" data-testid="error-message">
                    {error.message}
                  </small>
                )}
              </>
            );
          }}
        />
      </div>

      {/* Active period section */}
      <div className="mb-5">
        <div className="mb-4 border-b border-neutral-200 pb-2 text-xs text-neutral-500">ACTIVE PERIOD</div>
        <div className="mb-4 flex">
          <Checkbox {...register('setTimePeriod')} role="checkbox" checked={setTimePeriod} />
          <Text variant="body-1" color="default" className="ml-2">
            Set campaign time period
          </Text>
        </div>
        <div className="mb-4 flex flex-col gap-2 text-neutral-500">
          <Text as="span">
            Without setting a time period, this campaign will run indefinitely starting tomorrow. It can be configured at a later time.
          </Text>
          <Text as="span">To get images immediately after setting up campaign, restart Moka APP on your tablet.</Text>
        </div>
        <div className="flex items-center gap-2">
          <Text className="text-neutral-700">Select date</Text>
          <Controller
            control={control}
            name="timePeriod"
            render={({ field: { onChange, value } }) => {
              return (
                <DatePicker
                  dates={[today(), null]}
                  startDate={value.startDate}
                  endDate={value.endDate}
                  onChangeDate={(startDate, endDate) => onChange({ startDate, endDate })}
                  rangePicker
                  allDay
                  disabled={!setTimePeriod}
                />
              );
            }}
          />
        </div>
      </div>

      {/* Assign outlet section */}
      <div className="mb-3">
        <div className="mb-2 border-b border-neutral-200 pb-1 text-xs text-neutral-500">ASSIGNED OUTLETS</div>

        <Controller
          control={control}
          name="outletIds"
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <>
                <ul className="mb-3 flex flex-col gap-y-2 pl-2 text-neutral-700" data-testid="assigned-outlets">
                  {value.map((outletId) => (
                    <li key={outletId}>{mappedOutlets[outletId]?.name}</li>
                  ))}
                </ul>
                <OutletSelectorV2Modal
                  minItems={1}
                  selectedIds={value}
                  canSelectAll
                  onSubmit={(outlets) => {
                    const outletIds = [...outlets].map((outlet) => outlet.id);

                    onChange(outletIds);
                  }}
                >
                  <Button className="mb-3 w-full" data-testid="assign-outlet-form">
                    Assign Outlet
                  </Button>
                </OutletSelectorV2Modal>
                {error && (
                  <small className="text-red-500" data-testid="error-message">
                    {error.message}
                  </small>
                )}

                {errorOutlets?.length > 0 && <MaxCampaignError outlets={mappedErrorOutlets} />}
              </>
            );
          }}
        />
      </div>
    </div>
  );
}

export default CustomerDisplayForm;
